import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import type { components } from '../../lib/api/schema.gen'
import { sourceBadgeLabel } from '../catalog/format'
import { CART_MAX_LINE_QUANTITY } from './cart-limits'
import { type SkuResolveService, defaultSkuResolveService } from './sku-resolver'

type Sourceable = components['schemas']['Sourceable']
type Quantity = components['schemas']['Quantity']

/** Inline feedback under the add-row (mutually exclusive). `error`/`needs-config`
 * render as `role="alert"` (the field's `aria-describedby` points at them);
 * `success` renders as a non-interrupting `role="status"`. */
type AddLineFeedback =
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'needs-config'; readonly message: string; readonly partNumber: string }
  | { readonly kind: 'success'; readonly message: string }

export interface AddLineFormProps {
  readonly disabled?: boolean
  readonly onAdd: (sourceable: Sourceable, quantity: Quantity) => Promise<void>
  /**
   * `'empty-cart'` renders the compact horizontal row used by the Cart
   * empty state (UID-60 design match): a wider SKU input with a
   * catalog-oriented placeholder, visually-hidden (sr-only) field labels, a
   * "+ Add" primary submit button, and a "Search the full catalog" link
   * below the row. `'in-cart-row'` renders the compact horizontal control
   * that lives INSIDE the line table (below the column header) when the
   * header "Add parts" button is toggled — sr-only labels, a full-width
   * part-number input, and a "+ Add" primary submit, but (unlike
   * `'empty-cart'`) no leading search icon and no catalog link, since the
   * table row already provides the leading tag glyph and framing. Defaults
   * to `'default'`, the stacked layout — unchanged from before this prop
   * existed.
   */
  readonly variant?: 'default' | 'empty-cart' | 'in-cart-row'
  /**
   * The cross-source part-number resolver (MISUMI/partner catalog + Fictiv
   * parts library, queried in parallel). Injectable for tests (mirrors every
   * other vertical's `service` prop); defaults to the real BFF-backed
   * {@link defaultSkuResolveService}.
   */
  readonly resolver?: SkuResolveService
}

/**
 * The Cart screen's "add row" control (screens.md Cart §12). Resolves a typed
 * part number across BOTH catalogs — MISUMI/partner via the catalog service
 * and Fictiv custom via the parts library — through the shared
 * {@link SkuResolveService}, then hands a resolved Sourceable + Quantity up to
 * the caller, which owns the actual `POST /cart/lines` call. A configurable
 * family is NOT added blindly: it surfaces a "configure it first" note with a
 * link into the catalog (mirroring `ResultCard`'s "Configure" affordance).
 */
export function AddLineForm({
  disabled = false,
  onAdd,
  variant = 'default',
  resolver = defaultSkuResolveService,
}: AddLineFormProps) {
  const isEmptyCart = variant === 'empty-cart'
  const isInCartRow = variant === 'in-cart-row'
  // Both non-default variants share the compact horizontal layout: sr-only
  // field labels and a "+ Add" primary submit.
  const isHorizontal = isEmptyCart || isInCartRow
  const [sku, setSku] = useState('')
  const [quantityText, setQuantityText] = useState('1')
  const [feedback, setFeedback] = useState<AddLineFeedback>()
  const [submitting, setSubmitting] = useState(false)
  const skuId = useId()
  const qtyId = useId()
  const errorId = useId()

  const isError = feedback?.kind === 'error' || feedback?.kind === 'needs-config'

  // Clear any stale feedback as soon as either field is edited — the same
  // immediate-dismiss behavior the fields had before, now covering the
  // success/needs-config notes too.
  function onFieldEdit() {
    if (feedback) setFeedback(undefined)
  }

  async function handleAdd() {
    setFeedback(undefined)

    // Validate the quantity locally first — fail fast before any network
    // round-trip on an obviously-bad qty.
    const quantityValue = Number(quantityText)
    if (!Number.isFinite(quantityValue) || quantityValue <= 0 || !Number.isInteger(quantityValue)) {
      setFeedback({ kind: 'error', message: 'Quantity must be a positive whole number.' })
      return
    }
    if (quantityValue > CART_MAX_LINE_QUANTITY) {
      // Pin the grouping locale so the ceiling always reads "100,000" (not the
      // runtime-locale-dependent "1,00,000" under en-IN, etc.).
      setFeedback({
        kind: 'error',
        message: `Quantity can't exceed ${CART_MAX_LINE_QUANTITY.toLocaleString('en-US')}.`,
      })
      return
    }

    const partNumber = sku.trim()
    if (partNumber === '') {
      setFeedback({ kind: 'error', message: 'Enter a part number.' })
      return
    }

    setSubmitting(true)
    try {
      // Resolve across BOTH catalogs (MISUMI/partner + Fictiv library) in one
      // shared call. Part numbers are unique, so a hit is a direct add.
      const outcome = await resolver.resolve(partNumber)

      if (outcome.status === 'not-found') {
        setFeedback({
          kind: 'error',
          message: `No part found for "${partNumber}".${sourceNote(outcome.failedSources)}`,
        })
        return
      }
      if (outcome.status === 'error') {
        setFeedback({
          kind: 'error',
          message: outcome.message || 'Could not look up that part. Please try again.',
        })
        return
      }
      if (outcome.status === 'needs-config') {
        setFeedback({
          kind: 'needs-config',
          partNumber,
          message: `"${partNumber}" is a configurable part — configure it before adding.`,
        })
        return
      }

      // Resolved: direct add via the caller's cart add path.
      const { sourceable, source } = outcome
      await onAdd(sourceable, { value: quantityValue, uom: sourceable.uom.saleUom })
      setSku('')
      setQuantityText('1')
      const name = sourceable.name ?? sourceable.catalogSku ?? partNumber
      const badge = sourceBadgeLabel(sourceable.provenance)
      setFeedback({
        kind: 'success',
        message: `Added ${name} · ${badge}.${sourceNote(outcome.failedSources)}`,
      })
      // A collision (both sources hit a supposedly-unique number) is a data
      // anomaly worth a console note; the exact-SKU catalog match was used.
      if (outcome.collision) {
        console.warn(
          `Part number "${partNumber}" matched both catalog and library; used the ${source} match.`,
        )
      }
    } catch {
      setFeedback({ kind: 'error', message: 'Could not add that item. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      className={[
        'cart-add-row-form',
        isEmptyCart && 'cart-add-row-form--inline',
        isInCartRow && 'cart-add-row-form--in-cart',
      ]
        .filter(Boolean)
        .join(' ')}
      onSubmit={(event) => {
        event.preventDefault()
        void handleAdd()
      }}
      noValidate
    >
      <fieldset disabled={disabled || submitting}>
        <legend className="sr-only">Add an item to your cart by SKU</legend>
        <div className="form-field">
          <label htmlFor={skuId} className={isHorizontal ? 'sr-only' : undefined}>
            SKU
          </label>
          {/* Empty-cart variant: the prototype's "Add by part number" field is a
              search-style input with an icon inset at its left edge (`Fictiv
              Platform.dc.html` ~L1939 — height 44 / radius 9 / left padding 36
              to clear the icon). The default (in-cart add-row) variant is
              unchanged — no wrapper, no icon, its existing bare-input styling
              stands as before. */}
          {isEmptyCart ? (
            <span className="cart-add-row-search-wrap">
              <TagIcon />
              <input
                id={skuId}
                type="text"
                className="cart-add-row-search-input"
                value={sku}
                onChange={(event) => {
                  setSku(event.target.value)
                  onFieldEdit()
                }}
                placeholder="Enter a MISUMI or Fictiv part number…"
                aria-invalid={isError ? true : undefined}
                aria-describedby={isError ? errorId : undefined}
              />
            </span>
          ) : (
            // Live-verification defect: this bare `<input>` had NO styling
            // class at all — a raw browser-chrome text field (2px inset
            // border, 0 radius, ~21px tall) inside an otherwise
            // token-styled page, "very basic" next to everything else on
            // the screen. Same `.input-control-lg` treatment as the
            // empty-cart variant/EditConfigurationModal/qty input below,
            // for visual consistency across every text input on this
            // screen.
            <input
              id={skuId}
              type="text"
              className={
                isInCartRow ? 'input-control-lg cart-add-row-part-input' : 'input-control-lg'
              }
              value={sku}
              onChange={(event) => {
                setSku(event.target.value)
                onFieldEdit()
              }}
              placeholder={isInCartRow ? 'Enter a MISUMI or Fictiv part number…' : 'e.g. B6800ZZ'}
              aria-invalid={isError ? true : undefined}
              aria-describedby={isError ? errorId : undefined}
            />
          )}
        </div>
        <div className="form-field">
          <label htmlFor={qtyId} className={isHorizontal ? 'sr-only' : undefined}>
            Quantity
          </label>
          <input
            id={qtyId}
            type="number"
            min={1}
            max={CART_MAX_LINE_QUANTITY}
            inputMode="numeric"
            className="input-control-lg"
            value={quantityText}
            onChange={(event) => {
              setQuantityText(event.target.value)
              onFieldEdit()
            }}
            aria-invalid={isError ? true : undefined}
            aria-describedby={isError ? errorId : undefined}
          />
        </div>
        <Button type="submit" variant={isHorizontal ? 'primary' : 'secondary'} loading={submitting}>
          {isHorizontal ? '+ Add' : 'Add to cart'}
        </Button>
        {/* While both source lookups run: an accessible inline spinner (the
            Button already flips `aria-busy`; this gives sighted users a
            visible "searching both catalogs" cue). */}
        {submitting && <Spinner size="sm" label="Searching the catalog and your parts library…" />}
      </fieldset>
      {isError && feedback && (
        <p id={errorId} role="alert" className="form-error-text">
          {feedback.message}
          {feedback.kind === 'needs-config' && (
            <>
              {' '}
              <Link to={`/catalog/search?q=${encodeURIComponent(feedback.partNumber)}`}>
                Configure it in the catalog →
              </Link>
            </>
          )}
        </p>
      )}
      {feedback?.kind === 'success' && (
        <output className="cart-add-row-success-text">{feedback.message}</output>
      )}
      {isEmptyCart && (
        <p className="cart-add-row-catalog-link">
          Need to find one? <Link to="/catalog">Search the full catalog →</Link>
        </p>
      )}
    </form>
  )
}

/** A trailing "(couldn't check the catalog/library)" note when one source
 * errored but the other still answered — so a partial outage is surfaced,
 * never silently hidden. Empty when both sources answered. */
function sourceNote(failedSources: readonly ('catalog' | 'library')[]): string {
  if (failedSources.length === 0) return ''
  const labels = failedSources.map((s) => (s === 'catalog' ? 'the catalog' : 'your parts library'))
  return ` (couldn’t check ${labels.join(' and ')} — it may be temporarily unavailable)`
}

/** Leading tag glyph inset in the empty-cart "Add by part number" field — the
 * design uses a part-number tag icon here, not a search magnifier. Positioned
 * by `.cart-add-row-search-icon` (cart.css). */
function TagIcon() {
  return (
    <svg
      aria-hidden="true"
      className="cart-add-row-search-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" x2="7.01" y1="7" y2="7" />
    </svg>
  )
}
